import { useMemo, useState } from "react";
import { Button, Input, Modal, Select, Tooltip, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitItemDto,
  DataKitItemPriceDto,
  DataKitMenuDto,
} from "../../../../domain/DataKitDto";
import "./DataKitOverrideTables.scss";

interface LocationLike {
  internalId?: number;
  name?: string;
  city?: string;
  state?: string;
}

/**
 * Human label for a location price a given menu will not be using.
 *
 * The customer-facing wording deliberately never says wins, resolves or override: a location price
 * simply covers every menu that has no price of its own. The chip states the exception; the full
 * sentence lives on hover.
 */
function exceptionCopy(menuNames: string[]): string {
  if (menuNames.length === 1) return `every menu except ${menuNames[0]}`;
  const last = menuNames[menuNames.length - 1];
  return `every menu except ${menuNames.slice(0, -1).join(", ")} and ${last}`;
}

/**
 * One menu's own price across every size, as the customer reads it: "$25 / $46". A size the menu
 * does not price itself shows the kit price, because that is what the menu will actually print --
 * a blank cell inherits, so quoting only the overridden sizes would misstate the menu.
 */
function menuPriceLabel(menuId: string, prices: DataKitItemPriceDto[]): string {
  return prices
    .map((p) => {
      const own = (p.menuOverrides || []).find((o) => o.menuId === menuId);
      return `${(own && own.price) || p.price || ""}`.trim();
    })
    .filter(Boolean)
    .join(" / ");
}

/**
 * The hover sentence behind the chip. Names the menus that price themselves, quotes what they
 * charge, and says plainly what the location price still covers.
 */
function exceptionSentence(priced: { name: string; label: string }[], locationName: string): string {
  const withPrice = priced.map((m) => (m.label ? `${m.name} (${m.label})` : m.name));
  if (withPrice.length === 1) {
    return `${withPrice[0]} has its own price. Every other ${locationName} menu shows this price.`;
  }
  const last = withPrice[withPrice.length - 1];
  const list = `${withPrice.slice(0, -1).join(", ")} and ${last}`;
  return `${list} have their own prices. Every other ${locationName} menu shows this price.`;
}

interface Props {
  kit: DataKitDto;
  item: DataKitItemDto;
  prices: DataKitItemPriceDto[];
  locations: LocationLike[];
  menus: DataKitMenuDto[];
  canWrite: boolean;
  /** Called after a price write lands, so the drawer can take the server's item back. */
  onItemChanged: (item: DataKitItemDto) => void;
  onLocationOverridesChanged: (next: DataKitItemPriceDto[]) => void;
}

/**
 * The two override tables under the item's own prices: one row per location, one row per menu, one
 * cell per size. A blank cell inherits, which is why the kit price sits in the column header as a
 * grey reference rather than being pre-filled into the input.
 */
export function DataKitOverrideTables({
  kit,
  item,
  prices,
  locations,
  menus,
  canWrite,
  onItemChanged,
  onLocationOverridesChanged,
}: Props) {
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [draftId, setDraftId] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((l) => {
      if (l.internalId != null) {
        const parts = [l.city, l.state].filter(Boolean);
        map.set(String(l.internalId), l.name || parts.join(", ") || `Location ${l.internalId}`);
      }
    });
    return map;
  }, [locations]);

  const menuNameById = useMemo(
    () => new Map(menus.filter((m) => m.id).map((m) => [m.id as string, m.name])),
    [menus]
  );

  /** Locations that already carry an override on at least one size. */
  const overriddenUnitIds = useMemo(() => {
    const ids = new Set<string>();
    prices.forEach((p) => (p.overrides || []).forEach((o) => ids.add(o.unitId)));
    return Array.from(ids).sort((a, b) =>
      (locationNameById.get(a) || a).localeCompare(locationNameById.get(b) || b)
    );
  }, [prices, locationNameById]);

  /** Menus that already carry a price on at least one size. */
  const overriddenMenuIds = useMemo(() => {
    const ids = new Set<string>();
    prices.forEach((p) => (p.menuOverrides || []).forEach((o) => ids.add(o.menuId)));
    return Array.from(ids).sort((a, b) =>
      (menuNameById.get(a) || a).localeCompare(menuNameById.get(b) || b)
    );
  }, [prices, menuNameById]);

  /** Menus this item prices itself — the exception a location row has to declare. */
  const pricedMenus = useMemo(
    () =>
      overriddenMenuIds.map((id) => ({
        name: menuNameById.get(id) || "a menu",
        label: menuPriceLabel(id, prices),
      })),
    [overriddenMenuIds, menuNameById, prices]
  );

  const pricedMenuNames = useMemo(() => pricedMenus.map((m) => m.name), [pricedMenus]);

  // The kit's own assigned-location count, not the account's location list: a location the kit does
  // not serve is not inheriting anything from it.
  const inheritingCount = Math.max(0, kit.locationCount - overriddenUnitIds.length);

  const setLocationCell = (unitId: string, size: string, value: string) => {
    onLocationOverridesChanged(
      prices.map((p) => {
        if (p.size !== size) return p;
        const existing = (p.overrides || []).find((o) => o.unitId === unitId);
        if (value.trim() === "") {
          return { ...p, overrides: (p.overrides || []).filter((o) => o.unitId !== unitId) };
        }
        if (existing) {
          return {
            ...p,
            overrides: (p.overrides || []).map((o) =>
              o.unitId === unitId ? { ...o, price: value } : o
            ),
          };
        }
        return {
          ...p,
          overrides: [...(p.overrides || []), { unitType: "LOCATION", unitId, price: value }],
        };
      })
    );
  };

  const removeLocationRow = (unitId: string) => {
    onLocationOverridesChanged(
      prices.map((p) => ({
        ...p,
        overrides: (p.overrides || []).filter((o) => o.unitId !== unitId),
      }))
    );
  };

  const commitMenuCell = (menuId: string, size: string, value: string) => {
    if (!item.id) return;
    setSaving(true);
    DataKitApi.setMenuPrice(item.id, { menuId, size, price: value.trim() || null })
      .then((res) => onItemChanged(res.data))
      .catch(() => message.error("Could not save that menu price"))
      .finally(() => setSaving(false));
  };

  const addLocationRow = () => {
    if (!draftId) return;
    // Seeded blank, not pre-filled with the kit price: a blank cell means "inherit", so pre-filling
    // would silently pin this location to today's kit price.
    onLocationOverridesChanged(
      prices.map((p, i) =>
        i === 0
          ? {
              ...p,
              overrides: [
                ...(p.overrides || []),
                { unitType: "LOCATION", unitId: draftId, price: "" },
              ],
            }
          : p
      )
    );
    setAddLocationOpen(false);
    setDraftId(undefined);
  };

  const addMenuRow = () => {
    if (!draftId || !item.id || prices.length === 0) return;
    setSaving(true);
    DataKitApi.setMenuPrice(item.id, {
      menuId: draftId,
      size: prices[0].size,
      price: prices[0].price,
    })
      .then((res) => {
        onItemChanged(res.data);
        setAddMenuOpen(false);
        setDraftId(undefined);
      })
      .catch(() => message.error("Could not add that menu price"))
      .finally(() => setSaving(false));
  };

  const sizeLabel = (p: DataKitItemPriceDto) => p.size?.trim() || "Price";

  return (
    <>
      <section className="data-kit-override-panel">
        <div className="override-panel-head">
          <span className="override-panel-title">Location Price Overrides</span>
          <span className="override-panel-hint">
            Per size. {inheritingCount} location{inheritingCount === 1 ? "" : "s"} inherit the library
            price.
          </span>
        </div>

        {overriddenUnitIds.length > 0 && (
          <div className="override-table">
            <div className="override-row override-head">
              <span className="override-cell-label">Location</span>
              {prices.map((p) => (
                <span className="override-cell" key={p.size}>
                  {sizeLabel(p)} <em>library {p.price || "--"}</em>
                </span>
              ))}
              <span className="override-cell-action" />
            </div>

            {overriddenUnitIds.map((unitId) => (
              <div className="override-row" key={unitId}>
                <span className="override-cell-label">
                  {locationNameById.get(unitId) || `Location ${unitId}`}
                  {pricedMenus.length > 0 && (
                    <Tooltip
                      title={exceptionSentence(
                        pricedMenus,
                        locationNameById.get(unitId) || "location"
                      )}
                    >
                      <span className="override-exception-chip">
                        {exceptionCopy(pricedMenuNames)}
                      </span>
                    </Tooltip>
                  )}
                </span>
                {prices.map((p) => {
                  const cell = (p.overrides || []).find((o) => o.unitId === unitId);
                  return (
                    <span className="override-cell" key={p.size}>
                      <Input
                        size="small"
                        value={cell?.price || ""}
                        placeholder={p.price || "--"}
                        disabled={!canWrite}
                        onChange={(e) => setLocationCell(unitId, p.size, e.target.value)}
                      />
                    </span>
                  );
                })}
                <span className="override-cell-action">
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => removeLocationRow(unitId)}
                      aria-label="Remove this location's prices"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {canWrite && (
          <Button
            size="small"
            className="override-add-btn"
            onClick={() => {
              setDraftId(undefined);
              setAddLocationOpen(true);
            }}
          >
            + Location override
          </Button>
        )}
      </section>

      <section className="data-kit-override-panel">
        <div className="override-panel-head">
          <span className="override-panel-title">Menu Price Overrides</span>
          <span className="override-panel-hint">
            Per size. A menu price is used wherever that menu appears.
          </span>
        </div>

        {overriddenMenuIds.length > 0 && (
          <div className="override-table">
            <div className="override-row override-head">
              <span className="override-cell-label">Menu</span>
              {prices.map((p) => (
                <span className="override-cell" key={p.size}>
                  {sizeLabel(p)} <em>library {p.price || "--"}</em>
                </span>
              ))}
              <span className="override-cell-action" />
            </div>

            {overriddenMenuIds.map((menuId) => (
              <div className="override-row" key={menuId}>
                <span className="override-cell-label">{menuNameById.get(menuId) || "Menu"}</span>
                {prices.map((p) => {
                  const cell = (p.menuOverrides || []).find((o) => o.menuId === menuId);
                  return (
                    <span className="override-cell" key={p.size}>
                      <Input
                        size="small"
                        defaultValue={cell?.price || ""}
                        placeholder={p.price || "--"}
                        disabled={!canWrite || saving}
                        onBlur={(e) => commitMenuCell(menuId, p.size, e.target.value)}
                        onPressEnter={(e) =>
                          commitMenuCell(menuId, p.size, (e.target as HTMLInputElement).value)
                        }
                      />
                    </span>
                  );
                })}
                <span className="override-cell-action">
                  {canWrite && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => prices.forEach((p) => commitMenuCell(menuId, p.size, ""))}
                      aria-label="Remove this menu's prices"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {canWrite && (
          <Button
            size="small"
            className="override-add-btn"
            onClick={() => {
              setDraftId(undefined);
              setAddMenuOpen(true);
            }}
          >
            + Menu override
          </Button>
        )}
      </section>

      <Modal
        open={addLocationOpen}
        width={520}
        className={"clean one"}
        onCancel={() => setAddLocationOpen(false)}
      >
        <header>
          <h2>Add a location price</h2>
        </header>
        <p className="override-modal-hint">
          Leave a size blank and that size keeps the library price at this location.
        </p>
        <Select
          className="override-modal-select"
          size="large"
          showSearch
          placeholder="Choose a location"
          value={draftId}
          onChange={setDraftId}
          optionFilterProp="label"
          options={locations
            .filter((l) => l.internalId != null && !overriddenUnitIds.includes(String(l.internalId)))
            .map((l) => ({
              label: l.name || [l.city, l.state].filter(Boolean).join(", "),
              value: String(l.internalId),
            }))}
        />

        <footer>
          <Button size="large" onClick={() => setAddLocationOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            disabled={!draftId}
            onClick={addLocationRow}
          >
            Add
          </Button>
        </footer>
      </Modal>

      <Modal
        open={addMenuOpen}
        width={520}
        className={"clean one"}
        onCancel={() => setAddMenuOpen(false)}
      >
        <header>
          <h2>Add a menu price</h2>
        </header>
        <p className="override-modal-hint">
          Starts from the library price so you can adjust it. This price is used wherever the menu
          appears.
        </p>
        <Select
          className="override-modal-select"
          size="large"
          showSearch
          placeholder="Choose a menu"
          value={draftId}
          onChange={setDraftId}
          optionFilterProp="label"
          options={menus
            .filter((m) => m.id && !overriddenMenuIds.includes(m.id))
            .map((m) => ({ label: m.name, value: m.id as string }))}
        />

        <footer>
          <Button size="large" onClick={() => setAddMenuOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={saving}
            disabled={!draftId}
            onClick={addMenuRow}
          >
            Add
          </Button>
        </footer>
      </Modal>
    </>
  );
}
